import { useSearchParams } from "react-router-dom";
import { useAccess } from "../../auth/useAccess";
import { AdminScreen, type AdminTab } from "./AdminScreen";
import { BusinessIntegrationsTab } from "./components/BusinessIntegrationsTab";
import { AgentsAdminTab } from "./components/AgentsAdminTab";

type TabId = "integrations" | "models";

/**
 * Copilot administration — the first of the canvas's two administration
 * screens, carrying Business integrations and Thinking models.
 *
 * Business Integrations is the GLOBAL MCP catalog, and it is deliberately not
 * visible to everyone who can use Copilot: it decides what data can be placed
 * in front of a language model. READ opens the tab, MANAGE enables the
 * controls inside it, and the backend enforces the same on every request.
 *
 * There is no `|| can(manage)` beside the read check, and its absence is the
 * point: `manage` confers `read` in `computeEffectiveAccess`, so the
 * implication is applied once, server-side, and this screen and the API guard
 * read the identical action set off /me. Re-adding the disjunction here would
 * reintroduce the thing it was compensating for — a UI that grants itself a
 * permission the backend does not agree it has.
 *
 * Thinking models sit beside it because the canvas puts them there: they are
 * standing instructions every user can pick, so maintaining the deployment's
 * set is an administrative act even though choosing one is not. The per-user
 * home in the Copilot settings dialog stays exactly as it was — the same
 * component, the same endpoints — so neither place is a copy of the other.
 *
 * That tab now also carries the internal AI agents, which were their own
 * top-level screen until they moved in behind its sub-tabs. The same reasoning
 * put them there: an agent is standing instructions a user picks in chat, so it
 * is the same object as a thinking model wearing more configuration, and an
 * administrator maintaining one is usually maintaining the other. Its edit
 * controls still ask `settings:edit`, exactly as the old screen's route did.
 */
export function CopilotAdminPage(): JSX.Element {
  const { can, atLeast } = useAccess();
  const [params, setParams] = useSearchParams();

  const canReadIntegrations = can("copilot.mcp_integrations_read");
  const canManageIntegrations = can("copilot.mcp_integrations_manage");
  // Maintaining the deployment's thinking models is an administrative act, so
  // it follows the administration screen rather than the Copilot module: a
  // user with Copilot access already edits models in the settings dialog.
  const canManageModels = atLeast("settings", "edit") || canManageIntegrations;

  const tabs: AdminTab<TabId>[] = [
    {
      id: "integrations",
      label: "Business integrations",
      lead: "Manage platform integrations and access.",
      visible: canReadIntegrations,
    },
    {
      id: "models",
      label: "Agents",
      lead: "The agents people can choose in chat: instructions, knowledge and tools.",
      visible: canManageModels,
      // Carries the agent studio's roster-plus-workspace frame, not a form.
      wide: true,
    },
  ];

  const visible = tabs.filter((tab) => tab.visible);
  const requested = params.get("tab") as TabId | null;
  const active =
    visible.find((tab) => tab.id === requested)?.id ?? visible[0]?.id ?? "integrations";

  if (visible.length === 0) {
    return (
      <AdminScreen<TabId>
        title="Copilot administration"
        tabs={tabs}
        active={active}
        onSelect={() => undefined}
      >
        <div className="set-list set-list-empty">
          <p className="field-error" role="alert">
            You do not have permission to administer Copilot.
          </p>
        </div>
      </AdminScreen>
    );
  }

  return (
    <AdminScreen<TabId>
      title="Copilot administration"
      tabs={tabs}
      active={active}
      // `replace` so tabbing around a settings screen does not fill the back
      // button with places the user never navigated to.
      onSelect={(id) => setParams({ tab: id }, { replace: true })}
    >
      {active === "integrations" && (
        <BusinessIntegrationsTab canManage={canManageIntegrations} />
      )}
      {active === "models" && <AgentsAdminTab />}
    </AdminScreen>
  );
}
